<template>
  <div>
    <v-card :loading="listLoading">
      <v-card-title>
        <v-row no-gutters>
          Repositories
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-btn class="primary_btn_v2 reg-btn" @click="register()" v-if="!isAdmin"> <v-icon>mdi-plus</v-icon> New Repository </v-btn>
        </v-row>
      </v-card-title>
      <v-card-text class="repo-body">
        <v-col class="mb-5 pt-0" cols="12" md="12" xs="12" sm="6">
          <v-data-table :items="repositoryList" :headers="getColumnHeaders()" :loading="listLoading" :custom-sort="customSort">
            <template v-if="isAdmin" v-slot:item.TeammemberName="{ item }">
              <strong>{{ item.TeammemberName }}</strong>
            </template>
            <template v-slot:item.CommitmentName="{ item }">
              <strong>{{ item.CommitmentName }}</strong>
            </template>
            <template v-slot:item.HttpsLink="{ item }">
              <span>{{ item.RepositoryHttpsLink }}</span>
            </template>
            <template v-slot:item.LastUpdateStatus="{ item }">
              <span v-html="getLastUpdate(item)" /> &nbsp;
              <v-btn v-if="item.LastUpdateStatus === lastUpdateStatus.Failed">
                <v-icon v-bind="attrs" v-on="on" color="error" style="cursor: pointer"> mdi-information </v-icon>
                <v-tooltip activator="parent" location="top" v-if="item.LastUpdateStatus === lastUpdateStatus.Failed">
                  <p class="failure-tooltip" v-html="item.LastUpdateError" />
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:item.ExpiryDate="{ item }">
              <span>{{ formatDate(item.ExpiryDate) }}</span>
            </template>
            <template v-slot:item.Actions="{ item }">
              <span class="operation text-center">
                <v-menu bottom left>
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" :color="`${isAdmin ? 'black' : 'white'}`">mdi-dots-vertical</v-icon>
                  </template>
                  <v-list>
                    <v-list-item @click="edit(item)" v-if="!isAdmin" prepend-icon="mdi-pencil" title="Edit" />
                    <v-list-item
                      @click="disableOrEnableTeammemberRepository(item)"
                      v-if="item.LastUpdateStatus === lastUpdateStatus.Disabled"
                      prepend-icon="mdi-toggle-switch-off"
                      title="Enable"
                    />
                    <v-list-item @click="disableOrEnableTeammemberRepository(item)" v-else prepend-icon="mdi-toggle-switch" title="Disable" />
                  </v-list>
                </v-menu>
              </span>
            </template>
          </v-data-table>
        </v-col>
      </v-card-text>
    </v-card>
    <v-dialog v-if="showRegisterRepo" v-model="showRegisterRepo" max-width="500" @keydown.esc="showRegisterRepo = false" :key="registerVersion">
      <repository-register
        ref="editRepo"
        :repoModel="selectedRepo"
        :commitments="commitments"
        :registerLoading="registerLoading"
        @closeEdit="closeEdit"
        @cancel="showRegisterRepo = false"
        @saved="repoSaved"
      ></repository-register>
    </v-dialog>
    <v-dialog persistent v-model="showRemovePopup" @keydown.esc="showRemovePopup = false" max-width="400px">
      <confirm-dialog @confirm="removeRepository" @cancel="showRemovePopup = false" :loading="isDeleting" :message="confirmModel"></confirm-dialog>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import Vue, { defineComponent } from "vue";
import RepositoryRegister from "./Register.vue";
import LoadingComponent from "../../components/Loading.vue";
import RepositoryService from "../../services/RepositoryService";
import ConfirmDialog from "../../components/ConfirmDialog.vue";
import { TeammemberRepositoryVM } from "../../viewModels/response/repository/TeammemberRepositoryVM";
import Utils from "../../utils/Utils";
import ListHelpers from "../../utils/ListHelper";
import Loading from "../../components/Loading.vue";
enum LastUpdateStatus {
  Failed,
  Succeeded,
  Expired,
  Disabled,
}
export default defineComponent({
  props: ["repositories", "pageLoading", "commitments", "isAdmin"],
  components: {
    RepositoryRegister,
    LoadingComponent,
    ConfirmDialog,
  },
  data() {
    return {
      lastUpdateStatus: LastUpdateStatus,
      registerVersion: 0,
      listLoading: true,
      registerLoading: false,
      repositoryList: [] as TeammemberRepositoryVM[],
      showRegisterRepo: false,
      showRemovePopup: false,
      isDeleting: false,
      confirmModel: {
        title: "Remove Repository",
        text: "Are you sure you want ro delete this repository ?",
      },
      selectedRepositoryIdForDelete: 0,
      selectedRepo: {} as TeammemberRepositoryVM,
      selectedRemoveRepo: {} as TeammemberRepositoryVM,
    };
  },
  methods: {
    getColumnHeaders() {
      const headers = [
        { title: "Commitment", sortable: true, value: "CommitmentName" },
        { title: "Https Link", sortable: true, value: "HttpsLink" },
        { title: "Last Update", sortable: true, value: "LastUpdateStatus" },
        { title: "Expiry Date", sortable: true, value: "ExpiryDate" },
        { title: "Actions", value: "Actions", sortable: false, align: "center" },
      ];

      if (this.isAdmin) {
        headers.unshift({ title: "Team Member", value: "TeammemberName", sortable: true });
      }

      return headers;
    },
    customSort(items: any, index: any, isDesc: any) {
      ListHelpers().sort(items, index[0], isDesc[0] ? "asc" : "desc");
      return items;
    },
    getLastUpdate(row: TeammemberRepositoryVM) {
      if (row.LastUpdateStatus === LastUpdateStatus.Succeeded) {
        return `<span class="green--text">Succeeded (${this.formatDate(row.LastUpdateDate)})</span>`;
      } else {
        let text = "Failed";
        let c = "red--text";
        if (row.LastUpdateStatus === LastUpdateStatus.Expired) {
          text = "Expired";
          c = "orange--text";
        }
        if (row.LastUpdateStatus === LastUpdateStatus.Disabled) {
          text = "Disabled";
          c = "grey--text text--darken-0";
        }
        if (row.LastUpdateDate) {
          text = text + ` (${this.formatDate(row.LastUpdateDate)})`;
        }

        return `<span class="${c}" style="font-weight:bold">${text}</span>`;
      }
    },
    formatDate(date: Date | null): string {
      if (date) {
        return Utils.toVsDateFormat(date);
      } else {
        return "Not set";
      }
    },
    fetchRepositories() {
      this.$emit("fetchRepositories");
    },
    closeEdit(message: any, raiseError = true) {
      if (raiseError) {
        this.errorRaised(message);
      }
      this.showRegisterRepo = false;
    },
    async repoSaved($model: any) {
      try {
        this.registerLoading = true;

        const result = await RepositoryService.createAndUpdate($model);

        if (!result.status) {
          this.errorRaised(result.message);
        } else {
          this.fetchRepositories();
          this.closeEdit("", false);
        }
      } finally {
        this.registerLoading = false;
      }
    },
    async register() {
      this.selectedRepo = {} as TeammemberRepositoryVM;
      this.showRegisterRepo = true;
      this.registerVersion++;
    },
    async edit($item: TeammemberRepositoryVM): Promise<void> {
      this.selectedRepo = $item;
      this.showRegisterRepo = true;
    },
    disableOrEnableTeammemberRepository($item: TeammemberRepositoryVM) {
      this.isDeleting = true;
      RepositoryService.disableOrEnableTeammemberRepository($item)
        .then(
          (response) => {
            this.fetchRepositories();
          },
          (error) => {
            this.isDeleting = false;
            this.errorRaised(error);
          }
        )
        .finally(() => {
          this.isDeleting = false;
          this.showRemovePopup = false;
        });
    },
    errorRaised(msg: string): void {
      this.$emit("errorRaised", msg);
    },
  },
  async mounted() {
    this.fetchRepositories();
  },
  watch: {
    async repositories(newVal) {
      this.selectedRepo = {} as TeammemberRepositoryVM;
      this.showRegisterRepo = false;
      this.repositoryList = newVal;
    },
    pageLoading(newVal) {
      this.listLoading = newVal;
    },
  },
});
</script>
<style scoped>
.v-tooltip__content {
  opacity: 1;
  max-width: 1000px !important;
  white-space: normal; /* Allow content to wrap to new lines */
  display: block;
}
@media (max-width: 429px) {
  .repo-body {
    padding-top: 50px;
  }
  .reg-btn {
    margin-top: 15px;
    width: 100%;
    /* margin-left: 30px; */
    /* width: 100%; */
  }
}
</style>
